import { useCallback, useEffect, useState } from "react";
import { useAtrasado } from "../componentes/atraso.js";
import { Link } from "react-router";
import { abaixoDaMateria, agruparPorMateria, ROTULO_DA_MATERIA, type ItemMarcado } from "@noesis/shared";
import { FalhaDaApi, pedir } from "../api.js";
import { Icone } from "../componentes/Icone.js";
import { IconeMarcado } from "../componentes/icones.js";
import { enunciadoLegivel } from "../rotulos.js";
import { useTrilhaAtiva } from "../estado/trilhas.js";

/**
 * A lista de marcados — o primeiro eixo da RN-07 a ganhar tela.
 *
 * A regra promete lista navegável por eixo desde a F1 e nenhuma existia: marcar
 * gravava e nada lia. Um marcador que não se pode reler é um botão que não faz
 * nada.
 *
 * **É aba do Acervo** (RN-60), ao lado de Trilhas, Importar e Fontes, e não um
 * destino novo na navegação. O acervo é o material de estudo e de onde ele vem;
 * o que a pessoa separou do material é parte disso. Um destino permanente para
 * uma lista que se abre de vez em quando é o que a RN-30 tirou da barra.
 *
 * **O agrupamento é por matéria** (RN-38), como em todo o resto, e ele usa
 * `agruparPorMateria`, que preserva a ordem de chegada: a lista vem do servidor
 * da marcação mais recente para a mais antiga, e é essa a ordem de dentro de
 * cada grupo.
 */

/**
 * Quanto de enunciado cabe numa linha de lista.
 *
 * Não é regra de negócio (invariante 4): é decisão de apresentação, do mesmo
 * tipo que a medida de leitura. O corte é por palavra, e não no meio de uma:
 * cortar "reprodu…" faz o leitor gastar a atenção remontando a palavra em vez de
 * reconhecendo a questão.
 */
const LIMITE_DO_ENUNCIADO = 160;

function truncar(texto: string): string {
  const limpo = texto.trim().replace(/\s+/g, " ");
  if (limpo.length <= LIMITE_DO_ENUNCIADO) return limpo;
  const corte = limpo.slice(0, LIMITE_DO_ENUNCIADO);
  const ultimoEspaco = corte.lastIndexOf(" ");
  return `${(ultimoEspaco > LIMITE_DO_ENUNCIADO / 2 ? corte.slice(0, ultimoEspaco) : corte).trimEnd()}…`;
}

type Estado =
  | { nome: "carregando" }
  | { nome: "erro"; mensagem: string }
  | { nome: "pronto"; marcados: ItemMarcado[] };

export function Marcados() {
  const trilha = useTrilhaAtiva();
  const [estado, setEstado] = useState<Estado>({ nome: "carregando" });
  const mostrarEsqueleto = useAtrasado(estado.nome === "carregando");
  const [desmarcando, setDesmarcando] = useState<string | null>(null);
  const [erroDaAcao, setErroDaAcao] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setEstado({ nome: "carregando" });
    try {
      setEstado({ nome: "pronto", marcados: (await pedir("item.marcados")).marcados });
    } catch (e) {
      setEstado({
        nome: "erro",
        mensagem: e instanceof FalhaDaApi ? e.message : "Não foi possível carregar os marcados.",
      });
    }
  }, []);

  useEffect(() => {
    if (!trilha) return;
    void carregar();
  }, [trilha, carregar]);

  /**
   * Desmarcar tira a linha da lista **em seguida à resposta do servidor**, e não
   * antes: a lista é curta e a ida é rápida, e uma linha que some e volta ao
   * falhar é pior que um instante de espera.
   */
  const [desmarcados, setDesmarcados] = useState<ReadonlySet<string>>(new Set());
  const [fechados, setFechados] = useState<ReadonlySet<string>>(new Set());

  async function desmarcar(itemId: string) {
    setDesmarcando(itemId);
    setErroDaAcao(null);
    try {
      // A marca alterna no servidor. O item fica na lista, esmaecido, com
      // "desfazer" — sair da lista no primeiro toque não deixaria voltar atrás.
      const r = await pedir("item.marcar", { params: { id: itemId } });
      setDesmarcados((d) => {
        const n = new Set(d);
        if (r.marcado) n.delete(itemId);
        else n.add(itemId);
        return n;
      });
    } catch (e) {
      setErroDaAcao(e instanceof FalhaDaApi ? e.message : "Não foi possível desmarcar.");
    } finally {
      setDesmarcando(null);
    }
  }

  if (!trilha) {
    return (
      <Moldura>
        <section className="estado">
          <h2 className="estado__titulo">Nenhuma trilha em uso</h2>
          <p className="estado__texto">
            A marca é do item, e o item é de uma trilha. Escolha uma para ver o que você separou nela.
          </p>
          <Link className="botao" to="/acervo?aba=trilhas">
            Escolher uma trilha
          </Link>
        </section>
      </Moldura>
    );
  }

  if (estado.nome === "carregando") {
    return (
      <Moldura>
        <div aria-busy="true" data-esqueleto={mostrarEsqueleto ? "visivel" : "mudo"}>
          <span className="so-leitor" role="status">
            Carregando os itens marcados
          </span>
          <div className="esqueleto esqueleto--faixa" />
        </div>
      </Moldura>
    );
  }

  if (estado.nome === "erro") {
    return (
      <Moldura>
        <div className="estado estado--erro" role="alert">
          <h2 className="estado__titulo">Não deu para carregar</h2>
          <p className="estado__texto">{estado.mensagem}</p>
          <button className="botao" type="button" onClick={() => void carregar()}>
            Tentar de novo
          </button>
        </div>
      </Moldura>
    );
  }

  if (estado.marcados.length === 0) {
    return (
      <Moldura>
        <section className="estado">
          <h2 className="estado__titulo">Nada marcado nesta trilha</h2>
          <p className="estado__texto">
            Marcar separa um item para voltar depois, sem dizer nada sobre acerto ou erro. O alvo fica na barra da tela
            de execução, em cima do enunciado.
          </p>
          <Link className="botao" to="/estudar">
            Estudar agora
          </Link>
        </section>
      </Moldura>
    );
  }

  const grupos = agruparPorMateria(estado.marcados, (m) => m.categoria);

  return (
    <Moldura>
      {erroDaAcao && (
        <p className="campo__erro" role="alert">
          {erroDaAcao}
        </p>
      )}

      <p className="t-corpo">
        {estado.marcados.length} {estado.marcados.length === 1 ? "item marcado" : "itens marcados"} em{" "}
        <strong>{trilha.nome}</strong>, do mais recente para o mais antigo.
      </p>

      {grupos.map((g) => {
        const chave = g.materia ?? " sem-materia";
        const aberto = !fechados.has(chave);
        return (
          <section className="marcados-grupo" key={chave}>
            <h2 className="marcados-grupo__titulo">
              <button
                type="button"
                className="marcados-grupo__alternar"
                aria-expanded={aberto}
                onClick={() =>
                  setFechados((f) => {
                    const n = new Set(f);
                    if (n.has(chave)) n.delete(chave);
                    else n.add(chave);
                    return n;
                  })
                }
              >
                <Icone glifo={aberto ? "chevron_down" : "chevron_right"} papel="acao" tamanho="p" />
                <span className="t-cartao">{g.materia ?? `Sem ${ROTULO_DA_MATERIA.toLowerCase()}`}</span>
                <span className="t-anotacao-p">({g.itens.length})</span>
              </button>
            </h2>
            {aberto && (
              <ul className="marcados-grupo__lista">
                {g.itens.map((m) => {
                  const abaixo = abaixoDaMateria(m.categoria);
                  const tirado = desmarcados.has(m.itemId);
                  return (
                    <li className={tirado ? "marcado-mao marcado-mao--tirado" : "marcado-mao"} key={m.itemId}>
                      {abaixo.length > 0 && <p className="t-anotacao-p">{abaixo.join(" · ")}</p>}
                      <p className="t-corpo marcado-mao__enunciado">{enunciadoLegivel(truncar(m.enunciado))}</p>
                      {m.arquivado && (
                        <p className="t-ajuda">Item arquivado: ele não entra mais em sessão, e a marca continua aqui até você tirá-la.</p>
                      )}
                      <button
                        type="button"
                        className="link-mao link-mao--tinta"
                        disabled={desmarcando === m.itemId}
                        onClick={() => void desmarcar(m.itemId)}
                      >
                        {tirado ? "desfazer" : "desmarcar"}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}
    </Moldura>
  );
}

/** A mesma moldura de painel das outras abas do Acervo: o título é do cabeçalho da tela. */
function Moldura({ children }: { children: React.ReactNode }) {
  return (
    <div className="tela tela--ampla tela--painel">
      <div className="tela__corpo">{children}</div>
    </div>
  );
}
